import { captureMessage, setUser } from '@sentry/angular';
import { Dto, logger } from '@damofire/data-commons';
import { getApp, initializeApp } from 'firebase/app';
import { browserLocalPersistence, getAuth, indexedDBLocalPersistence, initializeAuth } from 'firebase/auth';
import { DocumentSnapshot } from 'firebase/firestore';
import { OPERATION, Operation } from '../../des.dto';
import { IdbService } from '../../indexed/idb.service';
import { WAREHOUSE_SYNC_STEP_CLEAN, WAREHOUSE_SYNC_STEP_DOWNLOAD, WAREHOUSE_SYNC_STEP_PUSH, WarehouseSyncStepTaskContext } from '../executors';
import type { ProgressCallback } from '../executors';
import { WarehouseWorker } from '../worker';
import Timeout = NodeJS.Timeout;

export abstract class AbstractWarehouseSyncStepWorker<T extends Dto<string> = Dto<string>> extends WarehouseWorker {
  protected shouldStopTask = false;
  /**
   * Whether a pushed ADD operation is deleted once pushed. Turned off by workers whose ADD is only
   * finished by the main thread (the issue medias upload), which then deletes the operation itself.
   */
  protected readonly deletesPushedAddOperations: boolean = true;
  protected idbService: IdbService;
  protected userId: string;
  protected environment: any;

  protected constructor(
    protected readonly store: string,
    protected readonly display: string,
  ) {
    super();
  }

  async execute(
    environment: any,
    ctx: WarehouseSyncStepTaskContext,
    onProgress: (progress: number) => void,
    itemsProcessedCallback: (change: { data: T; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<void> {
    this.environment = environment;

    logger.debug('itemsProcessedCallback provided', { category: 'sync', provided: !!itemsProcessedCallback });
    return this.init().then(async () => {
      const { step, division, task } = ctx;
      logger.info(`Worker starting: ${step} - ${task} - ${division}`, { category: 'sync', step, task, division });

      if (!this.shouldStopTask) {
        switch (step) {
          case WAREHOUSE_SYNC_STEP_CLEAN:
            await this.clean(ctx, onProgress, itemsProcessedCallback);
            break;
          case WAREHOUSE_SYNC_STEP_DOWNLOAD:
            await this.download(ctx, onProgress, itemsProcessedCallback);
            break;
          case WAREHOUSE_SYNC_STEP_PUSH:
          default:
            await this.push(ctx, onProgress, itemsProcessedCallback);
        }
      }

      this.idbService.done().catch(console.error);
      await this.idbService.waitAllDone();
      logger.info(`Worker step complete: ${step} - ${task} - ${division}`, { category: 'sync' });
    });
  }

  async cancel(): Promise<void> {
    logger.info('Step cancel requested', { category: 'sync', store: this.store });
    this.shouldStopTask = true;
    this.idbService.done().catch(console.error);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected clean(
    ctx: WarehouseSyncStepTaskContext,
    onProgress?: (progress: number) => void,
    itemsProcessedCallback?: (change: { data: T; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<void> {
    return Promise.resolve();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected download(
    ctx: WarehouseSyncStepTaskContext,
    onProgress?: (progress: number) => void,
    itemsProcessedCallback?: (change: { data: T; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<void> {
    return Promise.resolve();
  }

  protected async push(
    ctx: WarehouseSyncStepTaskContext,
    onProgress?: (progress: number) => void,
    itemsProcessedCallback?: (change: { data: T; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<void> {
    const supportsOperations = await this.idbService.storeExists(`${this.store}-operations`);
    if (!supportsOperations) {
      logger.info('No operations store found', { category: 'sync', store: this.store });
      return;
    }

    await this.findDataToSync(itemsProcessedCallback, onProgress);
  }

  protected async deleteAllDataObsoleteFromStore<T extends Dto>(
    store: string,
    ctx: WarehouseSyncStepTaskContext,
    divisionResolver: (data: T) => string,
    itemsProcessedCallback?: (change: { data: T; operation: OPERATION }, buffer: ArrayBuffer) => void,
    onProgress?: ProgressCallback,
  ): Promise<void> {
    const allFromIndex: ({ syncId: string } & T)[] = await this.idbService.getAllFromIndex(store, 'division', ctx.division);
    const toDelete = allFromIndex.filter((a) => a.syncId !== ctx.syncId);

    if (toDelete.length) {
      if (itemsProcessedCallback) {
        toDelete.forEach((t) => itemsProcessedCallback({ data: t, operation: OPERATION.DELETE }, null));
      }
      const toDeleteKeys = toDelete.map((b) => [divisionResolver(b), b._id]);
      await this.idbService.deleteAll(store, toDeleteKeys);
      logger.info('Deleted obsolete data', { category: 'sync', count: toDelete.length, store, division: ctx.division });
    } else {
      logger.debug('Nothing to delete', { category: 'sync', store, division: ctx.division });
    }
    // The work is the scan itself; zero deletions is the happy path, not a shortfall.
    onProgress?.(100, { processed: allFromIndex.length, expected: allFromIndex.length });
  }

  async findDataToSync(
    itemsProcessedCallback?: (change: { data: T; operation: OPERATION }, buffer: ArrayBuffer) => void,
    onProgress?: ProgressCallback,
  ): Promise<void> {
    const operations = await this.idbService.getAll<Operation>(`${this.store}-operations`);
    onProgress?.(0, { processed: 0, expected: operations.length });
    const dataByOperation = await this.groupOperationsByKind(operations);

    let synced = 0;
    let syncErr = null;
    const batches: { kind: OPERATION; push: (items: (Operation | T)[]) => Promise<void> }[] = [
      { kind: OPERATION.ADD, push: (items) => this.addRemoteData(items as T[]) },
      { kind: OPERATION.DELETE, push: (items) => this.deleteRemoteData(items as T[]) },
      { kind: OPERATION.UPDATE, push: (items) => this.updateRemoteData(items as T[]) },
    ];
    for (const batch of batches) {
      try {
        synced += await this.pushOperationBatch(batch.kind, dataByOperation[batch.kind], operations, batch.push, itemsProcessedCallback);
      } catch (err) {
        syncErr = err;
      }
    }

    logger.info('Sync push completed', { category: 'sync', synced });
    onProgress?.(100, { processed: synced, expected: operations.length });

    if (syncErr) {
      throw syncErr;
    }
  }

  private async groupOperationsByKind(operations: Operation[]): Promise<Record<string, (Operation | T)[]>> {
    const dataByOperation: Record<string, (Operation | T)[]> = {};

    for (const operation of operations) {
      if (!dataByOperation[operation.operation]) {
        dataByOperation[operation.operation] = [];
      }

      if (operation.operation === OPERATION.DELETE) {
        dataByOperation[operation.operation].push(operation);
      } else {
        logger.debug('Resolving operation dto', { category: 'sync', id: operation._id });
        const dto = await this.idbService.getByKey<T & { syncId?: string }>(this.store, [operation.division._id, operation._id]);
        if (dto) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { syncId, ...dataWithoutSyncId } = dto;
          dataByOperation[operation.operation].push(dataWithoutSyncId as T);
        } else {
          logger.error('Operation entry missing linked object', { category: 'sync', store: this.store, id: operation._id });
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { syncId, ...dataWithoutSyncId } = operation.data as T & { syncId?: string };
          dataByOperation[operation.operation].push(dataWithoutSyncId as T);
        }
      }
    }

    return dataByOperation;
  }

  private async pushOperationBatch(
    kind: OPERATION,
    items: (Operation | T)[],
    operations: Operation[],
    push: (items: (Operation | T)[]) => Promise<void>,
    itemsProcessedCallback?: (change: { data: T; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<number> {
    if (!items?.length || this.shouldStopTask) {
      return 0;
    }

    try {
      await push(items);
      if (itemsProcessedCallback) {
        logger.info(`Forwarding ${kind.toUpperCase()} operations`, { category: 'sync', count: items.length });
        items.forEach((t) => itemsProcessedCallback({ data: t as T, operation: kind }, null));
      }
      // Every step passes a callback, so it can't tell which ADDs the main thread still has to finish:
      // the worker says so instead. A pushed ADD left behind is pushed again on every sync.
      if (kind !== OPERATION.ADD || this.deletesPushedAddOperations) {
        await this.deleteOperations(operations.filter((o: Operation) => o.operation === kind));
      }
      return items.length;
    } catch (err) {
      logger.error(`Failed to sync ${kind.toUpperCase()} operations`, { category: 'sync', error: err });
      throw err;
    }
  }

  protected addRemoteData(data: T[]): Promise<void> {
    throw new Error(`Not implemented ${data}`);
  }

  protected updateRemoteData(data: T[]): Promise<void> {
    throw new Error(`Not implemented ${data}`);
  }

  protected deleteRemoteData(data: T[]): Promise<void> {
    throw new Error(`Not implemented ${data}`);
  }

  private async deleteOperations(operations: Operation[]): Promise<void> {
    for (const o of operations) {
      await this.idbService.delete(`${this.store}-operations`, [o.division._id, o._id, o.operation]);
    }
  }

  private async init(): Promise<void> {
    this.idbService = new IdbService();

    let auth;
    if (this.environment.appConfig.watson.useWebworkers) {
      const app = initializeApp(this.environment.firebase);
      auth = initializeAuth(app, { persistence: [indexedDBLocalPersistence, browserLocalPersistence] });
      this.initSentry(this.environment);
    } else {
      auth = getAuth(getApp());
    }

    logger.info(`${this.display} worker initializing`, { category: 'sync' });
    logger.debug('Awaiting firebase auth in worker', { category: 'auth' });
    return new Promise((resolve, reject) => {
      let timeBomb: Timeout = null;
      const cb = auth.onAuthStateChanged(
        (user) => {
          if (user) {
            clearTimeout(timeBomb);
            logger.info('Worker firebase auth resolved', { category: 'auth' });
            setUser({ id: user.uid });
            this.userId = user.uid;

            resolve();
            cb();
          }
        },
        (err) => {
          reject();
          logger.error('Auth state change error in worker', { category: 'auth', error: err });
          captureMessage(`An error occurred while looking for auth state change events. [${err}]`, 'error');
        },
      );

      timeBomb = setTimeout(() => {
        logger.error('Firebase auth timeout in worker', { category: 'auth' });
        cb();
        reject();
      }, 5000);
    });
  }
}

export abstract class AbstractWarehouseDataSyncStepWorker<T = Dto<string>> extends AbstractWarehouseSyncStepWorker<T> {
  protected constructor(
    protected readonly store: string,
    protected readonly display: string,
  ) {
    super(store, display);
  }

  protected abstract resolveRemoteData(ctx: WarehouseSyncStepTaskContext, lastSnapshot: DocumentSnapshot, allFromIndex: T[]): Promise<[DocumentSnapshot, number]>;
  protected abstract updateTaskProgress(warehouse: string, downloaded: number, onProgress: ProgressCallback): Promise<void>;

  protected async download(ctx: WarehouseSyncStepTaskContext, onProgress?: ProgressCallback): Promise<void> {
    let fetchData = true;
    let lastSnapshot: DocumentSnapshot = null;
    let downloadedCnt = 0;

    // Report immediately so listeners show activity instead of silence until the first page returns.
    onProgress?.(0, { processed: 0 });

    const allFromIndex = await this.idbService.getAllFromIndex<T>(`${this.store}`, 'division', ctx.division);
    while (fetchData && !this.shouldStopTask) {
      const [snap, size] = await this.resolveRemoteData(ctx, lastSnapshot, allFromIndex);
      lastSnapshot = snap;
      downloadedCnt += size;
      logger.debug('Download snapshot fetched', { category: 'sync', hasMore: lastSnapshot != null, downloaded: downloadedCnt });
      fetchData = lastSnapshot != null;

      if (onProgress) {
        this.updateTaskProgress(ctx.division, downloadedCnt, onProgress).catch(console.error);
      }
    }
  }
}
